/* eslint-disable no-undef */
/* =============================================================================
 * Sensors (device health) + Connections (QuickBooks per manager)
 * ===========================================================================*/
import React, { useState, useEffect, useRef } from "react";
import { fmtNum, fmtHcf, fmtDate, fmtDateTime, timeAgo, initials, Icon, Pill, Battery, Signal, Drawer, DRow, Search, Avatar, useToast, ApiDown } from "./components.jsx";
import { DATA } from "./data.js";
import { fetchSensors, fetchSensorDetail, mapSensor, mapSensorDetail, syncMonnit, fetchMonnitStatus, fetchQboConnections, qboConnect, qboDisconnect, fetchQboRefCounts, refreshQboRef, importQboInvoices, fetchQboReconcile, renameQboCompany, fetchQboLinkProposals, applyQboLinks, clearReadCache } from "./api.js";

// Snapshot-friendly telemetry helpers: absolute times + cadence, and a schedule status vs the reference
// clock (the last sync) — instead of a wall-clock "X ago" that reads as live on frozen data.
const hasTime = (d) => d && d.getFullYear() > 2000;
function intervalLabel(last, next) {
  if (!hasTime(last) || !hasTime(next) || next <= last) return null;
  const min = (next - last) / 60000;
  if (min < 90) return `~${Math.max(1, Math.round(min))} min`;
  const h = min / 60;
  return h < 48 ? `~${Math.round(h)} h` : `~${Math.round(h / 24)} d`;
}
function schedule(next, ref) {
  if (!hasTime(next) || !ref) return null;
  const h = (next - ref) / 3600000;
  if (h >= 0) return { label: "on schedule", kind: "ok" };
  const over = -h;
  const amt = over < 48 ? `${Math.max(1, Math.round(over))} h` : `${Math.round(over / 24)} d`;
  return { label: `overdue ${amt}`, kind: over > 24 ? "danger" : "warn" };
}
// How long before the last sync the sensor last reported — the freshness signal on frozen snapshot data.
function beforeSync(last, ref) {
  if (!hasTime(last) || !ref) return "—";
  const min = (ref - last) / 60000;
  if (min < 90) return `${Math.max(1, Math.round(min))} min before sync`;
  const h = min / 60;
  return h < 48 ? `${Math.round(h)} h before sync` : `${Math.round(h / 24)} d before sync`;
}
// Monnit's own sensor health verdict (per support.monnit.com/article/411) — distinct from our last-heard
// "reporting" derivation: 0 OK · 1 Warning (battery/hardware) · 2 Alert (rule tripped) · 3 Inactive · 4/5 Sleeping.
const MONNIT_STATUS = {
  0: { label: "OK", color: "var(--color-success)" },
  1: { label: "Warning", color: "var(--color-warning-text)" },
  2: { label: "Alert", color: "var(--color-danger)" },
  3: { label: "Inactive", color: "var(--color-muted)" },
  4: { label: "Sleeping", color: "var(--color-muted)" },
  5: { label: "Sleeping", color: "var(--color-muted)" },
};
const monnitHealth = (status) => MONNIT_STATUS[status] || { label: "Unknown", color: "var(--color-muted)" };

// "Last reported" freshness chip: green ≤48h (on schedule) · yellow ≤30d (transient, may recover) · red beyond (not reporting).
const freshKind = (lastComm, ref) => {
  if (!lastComm || !ref) return "muted";
  const h = (ref.getTime() - lastComm.getTime()) / 3600000;
  return h <= 48 ? "ok" : h <= 24 * 30 ? "warn" : "danger";
};
const fmtStamp = (d) => {
  if (!d) return "never";
  const opts = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";   // disambiguate multi-year-old timestamps
  return d.toLocaleString("en-US", opts);
};

function Sensors({ mgr, params, openUnit }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("issues");   // exceptions-first: this screen answers "what needs me?", not "list every sensor"
  const [sel, setSel] = useState(params.sensorId || null);
  const [live, setLive] = useState(null);
  const [sync, setSync] = useState(null);      // live Monnit sync progress while a run is in flight
  const [refresh, setRefresh] = useState(0);   // bump to re-pull sensor health once a sync finishes
  const pollRef = useRef(null);
  const toast = useToast();
  useEffect(() => { if (params.sensorId) setSel(params.sensorId); }, [params.sensorId]);
  useEffect(() => () => clearTimeout(pollRef.current), []);   // stop polling if the screen unmounts

  // The sync runs as a background job, so poll it for real phase progress instead of firing a toast and
  // leaving you guessing. The job keeps running even if you navigate away — we just stop watching.
  const runSync = async () => {
    let r;
    try { r = await syncMonnit(); }
    catch { toast({ title: "Sync failed", body: "Couldn't reach the API.", err: true }); return; }
    if (!r.hasCredentials) { toast({ title: "Sync queued — no token yet", body: "Add a Monnit token to config to pull live data." }); return; }
    setSync({ state: "queued", phase: "Queued…", step: 0, totalSteps: 5 });
    const deadline = Date.now() + 10 * 60 * 1000;
    const tick = async () => {
      const p = (await fetchMonnitStatus().catch(() => null))?.sync;
      if (p) setSync(p);
      if (p && (p.state === "done" || p.state === "failed")) {
        toast({ title: p.state === "done" ? "Monnit sync complete" : "Monnit sync failed", body: p.message || "", err: p.state === "failed" });
        // The enqueue mutation cleared the cache minutes ago; the JOB just finished writing. Clear
        // again so the completion refetch can't repaint from a mid-job cache entry.
        clearReadCache();
        setRefresh((n) => n + 1);
        pollRef.current = setTimeout(() => setSync(null), 8000);   // leave the result on screen briefly
        return;
      }
      if (Date.now() > deadline) { setSync(null); return; }
      pollRef.current = setTimeout(tick, 1500);
    };
    pollRef.current = setTimeout(tick, 700);
  };
  useEffect(() => {
    let cancelled = false;
    fetchSensors(mgr && mgr !== "all" ? mgr : null)
      .then((rows) => {
        const newest = Math.max(0, ...rows.map((r) => (r.lastComm ? new Date(r.lastComm).getTime() : 0)));
        if (!cancelled) setLive(rows.map((r) => mapSensor(r, newest)));
      })
      .catch(() => { if (!cancelled) setLive(false); });
    return () => { cancelled = true; };
  }, [mgr, refresh]);

  const usingLive = Array.isArray(live);
  const pick = (id) => setSel(id);

  // Deployed builds: an API failure must be an honest error, not mock data that looks live.
  if (!live && !import.meta.env.DEV) return <ApiDown loading={live !== false} />;

  // Only pulse-counter water meters are billable; hide water-heater / other Monnit sensor types.
  const isMeter = (s) => s.isPulseCounter !== false;
  let sensors = (usingLive ? live : DATA.sensorsFor(mgr)).filter(isMeter);
  if (filter === "assigned") sensors = sensors.filter((s) => !s.retired);
  else if (filter === "unassigned") sensors = sensors.filter((s) => s.retired);
  // Attention filters count ASSIGNED meters only, matching the KPIs, the nav badge, and the "spares excluded"
  // caption (an unassigned device being offline is expected — browse those under Unassigned / All).
  else if (filter === "issues") sensors = sensors.filter((s) => (s.offline || s.lowBatt) && !s.retired);
  else if (filter === "lowbatt") sensors = sensors.filter((s) => s.lowBatt && !s.retired);
  if (q) { const s = q.toLowerCase(); sensors = sensors.filter((x) => String(x.sensorId).includes(s) || (x.propName || x.sensorName || x.name || "").toLowerCase().includes(s) || (x.unitNames || "").toLowerCase().includes(s)); }
  // Group the filtered sensors by property; unassigned devices bucket under "Unassigned" (sorted last).
  const sGroups = {};
  sensors.forEach((s) => { const k = s.propName || " "; (sGroups[k] = sGroups[k] || { name: s.propName || "Unassigned", items: [] }).items.push(s); });
  const sGroupList = Object.values(sGroups).sort((a, b) => (a.name === "Unassigned") - (b.name === "Unassigned") || a.name.localeCompare(b.name));

  const all = (usingLive ? live : DATA.sensorsFor(mgr)).filter(isMeter);
  // Snapshot data has no live feed, so measure "X ago" against the newest reading (the last sync), not wall-clock.
  const syncRef = usingLive ? new Date(Math.max(0, ...all.map((s) => (s.lastComm ? s.lastComm.getTime() : 0)))) : DATA.NOW;
  // Health only counts metering sensors (attached to a unit). Devices with no units are spare/retired —
  // being offline is expected for them, so they don't inflate the "not reporting" numbers.
  const metering = all.filter((s) => !s.retired);
  const reporting = metering.filter((s) => !s.alertsActive).length;
  const stale = metering.filter((s) => s.offline).length;
  const low = metering.filter((s) => s.lowBatt).length;
  const retiredCount = all.length - metering.length;

  const statusPill = (s) => s.retired ? <Pill kind="muted">No units</Pill> : s.offline ? <Pill kind="danger">Not reporting</Pill> : s.lowBatt ? <Pill kind="warn">Low battery</Pill> : <Pill kind="ok">Reporting</Pill>;
  const syncBusy = !!sync && (sync.state === "queued" || sync.state === "running");

  return (
    <div className="page">
      <div className="page__head">
        {/* syncRef is the newest device HEARTBEAT, not the sync job's run time — "as of last sync"
            read as "the sync is stale" when the nightly job was in fact running fine. */}
        <div><h1>Sensor health</h1><p>{metering.length} assigned{retiredCount ? ` · ${retiredCount} unassigned` : ""} · {all.length} Monnit devices{usingLive ? " · Live (API) · newest device report " + fmtDateTime(syncRef) : " · last sync " + timeAgo(DATA.addHours(DATA.NOW, -4))}</p></div>
        <div className="right"><button className="btn btn--primary" disabled={syncBusy} onClick={runSync}><Icon name="refresh" size={16} />{syncBusy ? "Syncing…" : "Sync Monnit now"}</button></div>
      </div>

      {sync && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 12 }}>
          <div className="between" style={{ alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              {sync.state === "done" ? "Sync complete" : sync.state === "failed" ? "Sync failed" : `Syncing Monnit — ${sync.phase}`}
            </span>
            {syncBusy && <span className="muted" style={{ fontSize: 11.5 }}>Step {sync.step || 0} of {sync.totalSteps || 5}</span>}
          </div>
          <div style={{ height: 6, borderRadius: 3, background: "var(--color-border-light)", overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 3, transition: "width .4s ease",
              width: `${Math.round(((sync.state === "done" ? (sync.totalSteps || 5) : sync.step || 0) / (sync.totalSteps || 5)) * 100)}%`,
              background: sync.state === "failed" ? "var(--color-danger)" : sync.state === "done" ? "var(--color-success)" : "var(--color-primary)",
            }} />
          </div>
          {(sync.message || sync.sensors > 0) && (
            <div className="muted" style={{ fontSize: 11.5, marginTop: 7 }}>
              {sync.message || `${sync.sensors} sensors${sync.pulls ? ` · ${sync.pulls} message pulls` : ""}${sync.readings ? ` · ${sync.readings} readings` : ""}`}
            </div>
          )}
        </div>
      )}

      <div className="between" style={{ margin: "2px 2px 8px", alignItems: "baseline" }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--color-muted)" }}>Assigned meter health</span>
        {retiredCount > 0 && <span className="muted" style={{ fontSize: 11.5 }}>{retiredCount} unassigned spare{retiredCount === 1 ? "" : "s"} excluded</span>}
      </div>
      <div className="kpis" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
        <div className="kpi"><div className="kpi__label"><span className="ic" style={{ background: "var(--color-success-bg)", color: "var(--color-success)" }}><Icon name="sensor" size={17} /></span>Reporting</div><div className="kpi__value">{reporting}<small> / {metering.length}</small></div><div className="kpi__sub">on schedule</div></div>
        <div className="kpi"><div className="kpi__label"><span className="ic" style={{ background: "var(--color-danger-bg)", color: "var(--color-danger)" }}><Icon name="alert" size={17} /></span>Not reporting</div><div className="kpi__value">{stale}</div><div className="kpi__sub">no comms in 48h+</div></div>
        <div className="kpi"><div className="kpi__label"><span className="ic" style={{ background: "var(--color-warning-bg)", color: "var(--color-warning-text)" }}><Icon name="battery" size={17} /></span>Low battery</div><div className="kpi__value">{low}</div><div className="kpi__sub">below 35% charge</div></div>
      </div>

      <div className="tbl-wrap">
        <div className="tbl-tools">
          <Search value={q} onChange={setQ} placeholder="Search sensor ID or property…" />
          <div className="seg">
            {[["issues", "Needs attention"], ["lowbatt", "Low battery"], ["assigned", "Assigned"], ["unassigned", "Unassigned"], ["all", "All"]].map(([k, l]) => <button key={k} className={filter === k ? "on" : ""} onClick={() => setFilter(k)}>{l}</button>)}
          </div>
          <div className="spacer" /><span className="count">{sensors.length} shown</span>
        </div>
        <table className="tbl">
          <thead><tr><th>Sensor</th><th>Units</th><th>Battery</th><th>Signal</th><th className="num">Current read (HCF)</th><th>Last reported</th><th style={{ width: 30 }}></th></tr></thead>
          <tbody>
            {sGroupList.map((g) => (
              <React.Fragment key={g.name}>
                <tr><td colSpan={7} style={{ background: "var(--color-gray-50)", fontWeight: 700, fontSize: 12.5, padding: "8px 12px" }}>{g.name}<span className="muted" style={{ fontWeight: 500 }}> · {g.items.length} sensor{g.items.length > 1 ? "s" : ""}</span></td></tr>
                {g.items.map((s) => (
                  <tr key={s.sensorId} onClick={() => pick(s.sensorId)}>
                    <td><div className="cell-2"><b className="mono">{s.sensorId}</b><span>App {s.monnitAppId}</span></div></td>
                    <td>{s.unitNames ? <span className="mono" style={{ fontSize: 12.5 }}>{s.unitNames}</span> : <span className="muted" style={{ fontSize: 12 }}>{(s.sensorName || "").replace(/^\s*water\s*(?:meter)?\s*-?\s*/i, "") || "—"}</span>}</td>
                    <td><Battery level={s.battery} /></td>
                    <td><Signal strength={s.signal} /></td>
                    <td className="num mono">{fmtNum(s.currentReading)}</td>
                    <td><Pill kind={freshKind(s.lastComm, syncRef)}>{fmtStamp(s.lastComm)}</Pill></td>
                    <td onClick={(e) => e.stopPropagation()}><button className="rowmenu" onClick={() => pick(s.sensorId)}><Icon name="chevron" size={16} /></button></td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {sGroupList.length === 0 && <tr><td colSpan={7} className="muted" style={{ textAlign: "center", padding: 24 }}>{filter === "issues" && !q ? "Nothing needs attention — every assigned meter is reporting on schedule. Use the filters above to browse all devices." : "No sensors match."}</td></tr>}
          </tbody>
        </table>
      </div>

      {usingLive
        ? <LiveSensorDrawer sensor={sel != null ? live.find((x) => x.sensorId === sel) : null} now={syncRef} onClose={() => setSel(null)} openUnit={openUnit} />
        : <SensorDrawer sensor={sel ? DATA.sensor(sel) : null} onClose={() => setSel(null)} openUnit={openUnit} />}
    </div>
  );
}

function SensorDrawer({ sensor, onClose, openUnit }) {
  const toast = useToast();
  if (!sensor) return null;
  const s = sensor;
  const units = s.unitIds.map((id) => DATA.unit(id)).filter(Boolean);
  return (
    <Drawer open={!!sensor} onClose={onClose}>
      <div className="drawer__head">
        <div className="av av-sq" style={{ background: s.offline ? "var(--color-danger)" : s.lowBatt ? "var(--color-warning-text)" : "var(--color-primary)" }}><Icon name="sensor" size={20} /></div>
        <div style={{ flex: 1 }}>
          <h2 className="mono" style={{ fontSize: "var(--font-size-lg)" }}>Sensor {s.sensorId}</h2>
          <div className="sub">{s.name} · Monnit app {s.monnitAppId}</div>
          <div className="wrap-gap" style={{ marginTop: 9 }}>{s.offline ? <Pill kind="danger">Not reporting</Pill> : s.lowBatt ? <Pill kind="warn">Low battery</Pill> : <Pill kind="ok">Reporting</Pill>}</div>
        </div>
        <button className="drawer__close" onClick={onClose}>&times;</button>
      </div>
      <div className="drawer__body">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="card" style={{ padding: 14, boxShadow: "none", border: "1px solid var(--color-border-light)" }}><div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Battery</div><div style={{ marginTop: 8 }}><Battery level={s.battery} /></div></div>
          <div className="card" style={{ padding: 14, boxShadow: "none", border: "1px solid var(--color-border-light)" }}><div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Signal</div><div style={{ marginTop: 8 }}><Signal strength={s.signal} /></div></div>
        </div>
        <div className="section-h">Telemetry</div>
        <DRow k="Current reading (HCF)"><span className="mono">{fmtNum(s.currentReading)}</span></DRow>
        <DRow k="Last synced">{hasTime(DATA.NOW) ? fmtDateTime(DATA.NOW) : "—"}</DRow>
        <DRow k="Last reported">{hasTime(s.lastComm)
          ? <Pill kind={freshKind(s.lastComm, DATA.NOW)}>{fmtDateTime(s.lastComm)}</Pill>
          : "—"}</DRow>
        <DRow k="Reporting interval">{intervalLabel(s.lastComm, s.nextComm) || "—"}</DRow>
        <DRow k="Counters">{s.counters.sort().join(", ")}</DRow>
        <DRow k="Monnit status">{(() => { const h = monnitHealth(s.status); return <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: h.color }} />{h.label} <span className="muted">({s.status})</span></span>; })()}</DRow>

        <div className="section-h">Units on this meter</div>
        <div className="muted" style={{ fontSize: 11.5, marginTop: -4, marginBottom: 8, lineHeight: 1.4 }}>Units whose water bill is calculated from this meter's readings (a meter can feed several units, one per counter).</div>
        {units.map((u) => (
          <div key={u.id} className="between" style={{ padding: "10px 0", borderBottom: "1px solid var(--color-border-light)", cursor: "pointer" }} onClick={() => { onClose(); openUnit(u.id); }}>
            <div><b style={{ fontSize: 13 }}>{u.name}</b><div className="muted" style={{ fontSize: 12 }}>{u.tenant} · counter {u.counter}</div></div>
            <span className="row" style={{ gap: 8 }}><b style={{ fontVariantNumeric: "tabular-nums" }}>{u.pendingUsage} u</b><Icon name="chevron" size={15} /></span>
          </div>
        ))}
      </div>
      <div className="drawer__foot">
        <button className="btn btn--ghost" onClick={() => toast({ title: "Reading requested", body: `On-demand read requested from sensor ${s.sensorId}.` })}><Icon name="refresh" size={15} />Request reading</button>
        <div style={{ flex: 1 }} />
        {s.lowBatt && <button className="btn btn--soft" onClick={() => toast({ title: "Battery flagged", body: "Replacement task created." })}><Icon name="battery" size={15} />Flag battery</button>}
      </div>
    </Drawer>
  );
}

// Live sensor drawer — health comes from the already-loaded row; counters + units are fetched.
function LiveSensorDrawer({ sensor, now, onClose, openUnit }) {
  const toast = useToast();
  const [detail, setDetail] = useState(null);   // {counters, units} · "error" · null (loading)
  useEffect(() => {
    if (!sensor) { setDetail(null); return; }
    let c = false; setDetail(null);
    fetchSensorDetail(sensor.sensorId)
      .then((d) => { if (!c) setDetail(mapSensorDetail(d)); })
      .catch(() => { if (!c) setDetail("error"); });
    return () => { c = true; };
  }, [sensor ? sensor.sensorId : null]);
  if (!sensor) return null;
  const s = sensor;
  const loading = detail === null;
  const err = detail === "error";
  const counters = !loading && !err ? detail.counters : [];
  const units = !loading && !err ? detail.units : [];
  return (
    <Drawer open={!!sensor} onClose={onClose}>
      <div className="drawer__head">
        <div className="av av-sq" style={{ background: s.offline ? "var(--color-danger)" : s.lowBatt ? "var(--color-warning-text)" : "var(--color-primary)" }}><Icon name="sensor" size={20} /></div>
        <div style={{ flex: 1 }}>
          <h2 className="mono" style={{ fontSize: "var(--font-size-lg)" }}>Sensor {s.sensorId}</h2>
          <div className="sub">{s.name} · Monnit app {s.monnitAppId}</div>
          <div className="wrap-gap" style={{ marginTop: 9 }}>{s.offline ? <Pill kind="danger">Not reporting</Pill> : s.lowBatt ? <Pill kind="warn">Low battery</Pill> : <Pill kind="ok">Reporting</Pill>}</div>
        </div>
        <button className="drawer__close" onClick={onClose}>&times;</button>
      </div>
      <div className="drawer__body">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="card" style={{ padding: 14, boxShadow: "none", border: "1px solid var(--color-border-light)" }}><div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Battery</div><div style={{ marginTop: 8 }}><Battery level={s.battery} /></div></div>
          <div className="card" style={{ padding: 14, boxShadow: "none", border: "1px solid var(--color-border-light)" }}><div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Signal</div><div style={{ marginTop: 8 }}><Signal strength={s.signal} /></div></div>
        </div>
        <div className="section-h">Telemetry</div>
        <DRow k="Current reading (HCF)"><span className="mono">{s.currentReading != null ? fmtNum(s.currentReading) : "—"}</span></DRow>
        <DRow k="Last synced">{hasTime(now) ? fmtDateTime(now) : "—"}</DRow>
        <DRow k="Last reported">{hasTime(s.lastComm)
          ? <Pill kind={freshKind(s.lastComm, now)}>{fmtDateTime(s.lastComm)}</Pill>
          : "—"}</DRow>
        <DRow k="Reporting interval">{intervalLabel(s.lastComm, s.nextComm) || "—"}</DRow>
        <DRow k="Counters">{counters.length ? counters.join(", ") : (loading ? "…" : "—")}</DRow>
        <DRow k="Monnit status">{(() => { const h = monnitHealth(s.status); return <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: h.color }} />{h.label} <span className="muted">({s.status})</span></span>; })()}</DRow>

        <div className="section-h">Units on this meter</div>
        <div className="muted" style={{ fontSize: 11.5, marginTop: -4, marginBottom: 8, lineHeight: 1.4 }}>Units whose water bill is calculated from this meter's readings (a meter can feed several units, one per counter).</div>
        {loading && <div className="muted" style={{ fontSize: 12.5, padding: "6px 0" }}>Loading units…</div>}
        {err && <div style={{ fontSize: 12.5, padding: "6px 0", color: "var(--color-danger)" }}>Couldn't load unit detail.</div>}
        {!loading && !err && units.length === 0 && <div className="muted" style={{ fontSize: 12.5, padding: "6px 0" }}>No active units on this meter.</div>}
        {units.map((u, i) => (
          <div key={i} className="between" style={{ padding: "10px 0", borderBottom: "1px solid var(--color-border-light)", cursor: u.tenancyId ? "pointer" : "default" }}
               onClick={() => { if (u.tenancyId) { onClose(); openUnit(u.tenancyId); } }}>
            <div><b style={{ fontSize: 13 }}>{u.name}</b><div className="muted" style={{ fontSize: 12 }}>{u.tenant} · counter {u.counter}</div></div>
            <span className="row" style={{ gap: 8 }}><b style={{ fontVariantNumeric: "tabular-nums" }}>{u.usage != null ? fmtHcf(u.usage) + " HCF" : "—"}</b>{u.tenancyId ? <Icon name="chevron" size={15} /> : null}</span>
          </div>
        ))}
      </div>
      <div className="drawer__foot">
        <span className="muted" style={{ fontSize: 12 }}>Readings sync automatically from Monnit{s.lowBatt ? " · battery low — schedule a replacement" : ""}.</span>
      </div>
    </Drawer>
  );
}

// ------------------------------------------------------------ Connections (QBO)
// The "needs mapping" counts, as click-throughs to where each kind is actually fixed. Refresh only pulls the QBO
// catalogs; it can't assign a record's link. cust -> the unit's QBO customer; items -> a rate-template line item;
// loans -> the loan's QBO class/vendor/accounts.
function ReconLinks({ r, go }) {
  if (!r) return <b style={{ fontSize: 11.5 }}>—</b>;
  if (r.customers + r.items + r.loans === 0) return <b style={{ fontSize: 11.5, color: "var(--color-success)" }}>all mapped</b>;
  const dest = { properties: "Properties & Units", templates: "Rate Templates", mortgages: "Mortgages" };
  const link = (n, label, route) => n > 0 ? (
    <button key={label} onClick={() => go(route)} title={`Fix on ${dest[route]}`}
      style={{ background: "none", border: 0, padding: 0, cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: "var(--color-warning-text)", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 2 }}>
      {n} {label}
    </button>
  ) : null;
  return <span className="row" style={{ gap: 10 }}>{link(r.customers, "cust", "properties")}{link(r.items, "items", "templates")}{link(r.loans, "loans", "mortgages")}</span>;
}

// Review-and-link panel: every unlinked field in the realm with its exact-name match from the cached
// refs. Exact matches come pre-checked; ambiguous ones force a choice; missing ones are inert. One
// "Link selected" applies the batch — links are ids with financial consequences, so never silent.
function LinkReviewPanel({ realmId, companyName, onClose, onApplied }) {
  const toast = useToast();
  const [rows, setRows] = useState(null);     // null = loading · false = failed · [] = nothing to link
  const [sel, setSel] = useState({});         // rowKey -> chosen qboId (checked rows only)
  const [busy, setBusy] = useState(false);
  const rowKey = (r) => `${r.entity}|${r.entityId}|${r.field}`;

  useEffect(() => {
    let c = false;
    fetchQboLinkProposals(realmId).then((list) => {
      if (c) return;
      setRows(list || []);
      const s = {};
      (list || []).forEach((r) => { if (r.status === "exact") s[rowKey(r)] = r.matches[0].qboId; });
      setSel(s);
    }).catch(() => { if (!c) setRows(false); });
    return () => { c = true; };
  }, [realmId]);

  const toggle = (r) => setSel((s) => {
    const k = rowKey(r), n = { ...s };
    if (n[k]) delete n[k]; else if (r.matches.length) n[k] = r.matches[0].qboId;
    return n;
  });
  const choose = (r, qboId) => setSel((s) => { const n = { ...s }; if (qboId) n[rowKey(r)] = qboId; else delete n[rowKey(r)]; return n; });

  const apply = async () => {
    const links = (rows || []).filter((r) => sel[rowKey(r)])
      .map((r) => ({ entity: r.entity, entityId: r.entityId, field: r.field, qboId: sel[rowKey(r)] }));
    if (!links.length) return;
    setBusy(true);
    try {
      const res = await applyQboLinks(realmId, links);
      toast({ title: `Linked ${res.applied}`, body: res.skipped ? `${res.skipped} skipped — review them individually.` : "All selected links applied." });
      onApplied && (await onApplied());
      onClose();
    } catch { toast({ title: "Link failed", body: "The API rejected the batch — nothing was changed." }); }
    finally { setBusy(false); }
  };

  const GROUPS = [["tenancy", "Customers (tenancies)"], ["line", "Invoice line items"], ["loan", "Loans"]];
  const selCount = Object.keys(sel).length;
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="between" style={{ padding: "18px 22px", borderBottom: "1px solid var(--color-border-light)" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Suggested QuickBooks links</h2>
          <div className="muted" style={{ fontSize: 12.5 }}>{companyName || "Company"} · exact name matches are pre-checked; nothing is written until you apply</div>
        </div>
        <button className="iconbtn" title="Close" onClick={onClose}><Icon name="x" size={17} /></button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "10px 22px" }}>
        {rows === null ? <div className="empty">Looking for matches…</div>
        : rows === false ? <div className="empty">Couldn't load link proposals from the API.</div>
        : rows.length === 0 ? <div className="empty">Nothing needs linking — everything here already has a QuickBooks id.</div>
        : GROUPS.map(([ent, label]) => {
            const grp = rows.filter((r) => r.entity === ent);
            if (!grp.length) return null;
            return (
              <div key={ent} style={{ margin: "14px 0 4px" }}>
                <div className="muted" style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", margin: "0 0 6px" }}>{label}</div>
                {grp.map((r) => {
                  const k = rowKey(r), checked = !!sel[k];
                  return (
                    <div key={k} className="row" style={{ gap: 10, padding: "8px 10px", borderRadius: 8, background: checked ? "var(--accent-soft)" : "transparent", alignItems: "flex-start" }}>
                      <input type="checkbox" checked={checked} disabled={!r.matches.length} onChange={() => toggle(r)} style={{ marginTop: 3 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13 }}>
                          <b>{r.entityLabel}</b> <span className="muted">· {r.fieldLabel}</span>
                        </div>
                        <div style={{ fontSize: 12.5, marginTop: 1 }}>
                          <span className="mono" style={{ fontSize: 11.5 }}>{r.name}</span>
                          {r.status === "exact" && <span style={{ color: "var(--color-success)" }}> → {r.matches[0].name} <span className="muted">(exact match)</span></span>}
                          {r.status === "missing" && <span style={{ color: "var(--color-warning-text)" }}> — not found in QuickBooks; pick it manually on its screen</span>}
                          {r.status === "ambiguous" && (
                            <select className="input input--sm" style={{ marginLeft: 8, fontSize: 12 }} value={sel[k] || ""} onChange={(e) => choose(r, e.target.value)}>
                              <option value="">{r.matches.length} matches — choose…</option>
                              {r.matches.map((m) => <option key={m.qboId} value={m.qboId}>{m.name} · {m.qboId}</option>)}
                            </select>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
      </div>
      <div className="between" style={{ padding: "14px 22px", borderTop: "1px solid var(--color-border-light)" }}>
        <span className="muted" style={{ fontSize: 12.5 }}>{selCount} selected</span>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" disabled={busy || !selCount} onClick={apply}>{busy ? "Linking…" : `Link ${selCount} selected`}</button>
        </div>
      </div>
    </div>
  );
}

function Connections({ mgr, go }) {
  const toast = useToast();
  const [conns, setConns] = useState(null);   // live /api/qbo/connections (realms in scope) · null = loading
  const [counts, setCounts] = useState({});   // realmId -> cached ref counts { Customer: n, Item: n, ... }
  const [recon, setRecon] = useState({});     // realmId -> { customers, items, loans } unmapped to a QBO id
  const [busy, setBusy] = useState(null);     // realmId being disconnected/refreshed, or "connect" while starting OAuth
  const [renaming, setRenaming] = useState(null);   // realmId whose name is being edited inline
  const [renameVal, setRenameVal] = useState("");
  const [linkFor, setLinkFor] = useState(null);     // connection whose link-review panel is open

  const load = async () => {
    const rows = await fetchQboConnections(mgr && mgr !== "all" ? mgr : null).catch(() => []);
    setConns(rows || []);
    const c = {}, rec = {};
    await Promise.all((rows || []).map(async (r) => {
      c[r.realmId] = await fetchQboRefCounts(r.realmId).catch(() => ({}));
      rec[r.realmId] = await fetchQboReconcile(r.realmId).catch(() => null);
    }));
    setCounts(c); setRecon(rec);
  };
  useEffect(() => { load(); }, [mgr]);

  const daysLeft = (iso) => (iso ? Math.round((new Date(iso) - Date.now()) / 86400000) : null);
  const countsLabel = (m) => (m && Object.keys(m).length ? Object.entries(m).map(([k, v]) => `${k} ${v}`).join(" · ") : "none cached");

  const connect = async () => {
    if (mgr === "all") { toast({ title: "Pick a manager", body: "Switch to a specific manager to connect its QuickBooks company." }); return; }
    setBusy("connect");
    try {
      const r = await qboConnect(mgr);
      if (r.configured && r.url) window.location.href = r.url;   // hand off to Intuit's consent screen
      else toast({ title: "Not configured", body: "Add your QuickBooks app keys (Qbo:ClientId / Qbo:ClientSecret) to connect." });
    } catch { toast({ title: "Connect failed", body: "Could not start the QuickBooks connection." }); }
    finally { setBusy(null); }
  };

  const saveRename = async (c) => {
    const name = renameVal.trim();
    if (!name) return;
    try { await renameQboCompany(c.realmId, name); setRenaming(null); toast({ title: "Renamed", body: `Now "${name}".` }); await load(); }
    catch { toast({ title: "Rename failed", body: "Could not rename the company." }); }
  };

  const disconnect = async (c) => {
    setBusy(c.realmId);
    try { await qboDisconnect(c.realmId); toast({ title: "Disconnected", body: `${c.companyName || "Company"} unlinked from QuickBooks.` }); await load(); }
    catch { toast({ title: "Disconnect failed", body: "Could not disconnect." }); }
    finally { setBusy(null); }
  };

  const refresh = async (c) => {
    setBusy("refresh:" + c.realmId);
    try {
      const r = await refreshQboRef(c.realmId);
      if (r.synced) { const total = Object.values(r.counts || {}).reduce((a, b) => a + b, 0); toast({ title: "Lists refreshed", body: `Pulled ${total} records from QuickBooks.` }); await load(); }
      else toast({ title: "Not refreshed", body: r.note || "Could not refresh lists." });
    } catch { toast({ title: "Refresh failed", body: "Could not refresh QuickBooks lists." }); }
    finally { setBusy(null); }
  };

  // R8: backfill the company's historical QuickBooks invoices into local billing history.
  const importHistory = async (c) => {
    setBusy("import:" + c.realmId);
    try {
      const r = await importQboInvoices(c.realmId);
      if (r.synced) {
        const bits = [`Imported ${r.imported} invoice${r.imported === 1 ? "" : "s"}`, `${r.skippedExisting} already known`];
        if (r.unmatchedCustomer) bits.push(`${r.unmatchedCustomer} without a matching tenant`);
        if (r.periodCollisions) bits.push(`${r.periodCollisions} period collision${r.periodCollisions === 1 ? "" : "s"}`);
        toast({ title: "History imported", body: bits.join(" · ") + "." });
        await load();
      } else toast({ title: "Not imported", body: r.note || "Could not import invoice history." });
    } catch { toast({ title: "Import failed", body: "Could not import QuickBooks invoice history." }); }
    finally { setBusy(null); }
  };

  return (
    <div className="page">
      <div className="page__head">
        <div><h1>QuickBooks</h1><p>Connected companies (realms) in your scope</p></div>
        <button className="btn btn--primary" disabled={busy === "connect"} onClick={connect}>
          <Icon name="link" size={15} />{busy === "connect" ? "Connecting…" : "Connect a company"}
        </button>
      </div>

      {conns === null ? (
        <div className="card"><div className="empty">Loading connections…</div></div>
      ) : conns.length === 0 ? (
        <div className="card"><div className="empty">No QuickBooks companies connected yet.</div></div>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: "repeat(2,1fr)" }}>
          {conns.map((c) => {
            const dl = daysLeft(c.refreshExpiresAtUtc);
            const status = !c.connected ? "disconnected" : dl != null && dl < 20 ? "expiring" : "connected";
            const color = status === "disconnected" ? "var(--color-danger)" : status === "expiring" ? "var(--color-warning-text)" : "var(--color-success)";
            const bg = status === "disconnected" ? "var(--color-danger-bg)" : status === "expiring" ? "var(--color-warning-bg)" : "var(--color-success-bg)";
            return (
              <div key={c.realmId} className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div className="between" style={{ padding: "16px 18px", borderBottom: "1px solid var(--color-border-light)" }}>
                  <div className="row" style={{ gap: 11 }}>
                    <Avatar id={c.realmId} label={initials(c.companyName || "QB")} size={38} round={10} />
                    <div>
                      {renaming === c.realmId ? (
                        <div className="row" style={{ gap: 6 }}>
                          <input className="input input--sm" style={{ width: 150 }} autoFocus value={renameVal}
                            onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveRename(c); }} />
                          <button className="btn btn--soft btn--sm" onClick={() => saveRename(c)}>Save</button>
                          <button className="btn btn--ghost btn--sm" onClick={() => setRenaming(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div className="row" style={{ gap: 6 }}>
                          <span style={{ fontWeight: 700 }}>{c.companyName || "QuickBooks company"}</span>
                          <button className="iconbtn" title="Rename" onClick={() => { setRenaming(c.realmId); setRenameVal(c.companyName || ""); }}><Icon name="edit" size={13} /></button>
                        </div>
                      )}
                      <div className="muted" style={{ fontSize: 12 }}>Realm <span className="mono">{c.realmId}</span></div>
                    </div>
                  </div>
                  <span className="pill" style={{ background: bg, color }}><span className="pdot" style={{ background: "currentColor" }} />{status === "disconnected" ? "Disconnected" : status === "expiring" ? "Token expiring" : "Connected"}</span>
                </div>
                <div style={{ padding: "14px 18px" }}>
                  <div className="between" style={{ fontSize: 12.5, padding: "5px 0" }}>
                    <span className="muted">Refresh token</span>
                    <b style={{ color: status === "disconnected" ? "var(--color-danger)" : status === "expiring" ? "var(--color-warning-text)" : "var(--color-text)" }}>
                      {!c.connected ? "Not connected" : dl == null ? "Active" : dl < 0 ? "Expired" : `${dl} days left`}
                    </b>
                  </div>
                  <div className="between" style={{ fontSize: 12.5, padding: "5px 0" }}>
                    <span className="muted">QuickBooks lists</span>
                    <b className="mono" style={{ fontSize: 11.5 }}>{countsLabel(counts[c.realmId])}</b>
                  </div>
                  <div className="between" style={{ fontSize: 12.5, padding: "5px 0" }}>
                    <span className="muted" title="Records still missing a QuickBooks link. Assign it in each record (click a count) — Refresh only updates the catalogs above, it can't map them for you.">Needs QBO mapping</span>
                    <ReconLinks r={recon[c.realmId]} go={go} />
                  </div>
                </div>
                <div className="row" style={{ padding: "12px 18px", borderTop: "1px solid var(--color-border-light)", gap: 9 }}>
                  {(() => { const r = recon[c.realmId]; return r && (r.customers + r.items + r.loans) > 0 && (counts[c.realmId] && Object.keys(counts[c.realmId]).length > 0) ? (
                    <button className="btn btn--soft btn--sm" onClick={() => setLinkFor(c)}><Icon name="link" size={14} />Review &amp; link</button>
                  ) : null; })()}
                  {c.connected && (
                    <button className="btn btn--ghost btn--sm" disabled={busy === "refresh:" + c.realmId} onClick={() => refresh(c)}><Icon name="refresh" size={14} />{busy === "refresh:" + c.realmId ? "Refreshing…" : "Refresh lists"}</button>
                  )}
                  {/* Import history is one-time setup: prominent until the realm has QBO-linked invoices,
                      then demoted to an icon (re-runs still self-heal links / pick up new legacy docs). */}
                  {c.connected && !c.linkedInvoices && (
                    <button className="btn btn--ghost btn--sm" disabled={busy === "import:" + c.realmId} onClick={() => importHistory(c)}><Icon name="history" size={14} />{busy === "import:" + c.realmId ? "Importing…" : "Import history"}</button>
                  )}
                  {c.connected && (
                    <button className="btn btn--ghost btn--sm" disabled={busy === c.realmId} onClick={() => disconnect(c)}><Icon name="link" size={14} />{busy === c.realmId ? "Disconnecting…" : "Disconnect"}</button>
                  )}
                  {c.connected && c.linkedInvoices > 0 && (
                    <button className="iconbtn" style={{ marginLeft: "auto" }} disabled={busy === "import:" + c.realmId}
                            title="Re-import QuickBooks history — already done once; re-running picks up newly issued legacy documents and self-heals reading links"
                            onClick={() => importHistory(c)}><Icon name="history" size={15} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card" style={{ marginTop: 16, padding: "14px 18px", fontSize: 13, lineHeight: 1.55 }}>
        <b>How it works.</b> Each card is a QuickBooks company (realm). Connecting sends the selected manager to Intuit's consent screen for that company; the app stores the OAuth tokens and refreshes them automatically. A manager can bill into more than one company. Invoices are prepared as a dry run until the push is switched on in config. <b>Note:</b> with Development (sandbox) keys, Intuit can only connect <i>sandbox</i> companies — and if you're already signed in, it may auto-pick the previously-authorized one without showing a login. Connecting a live company requires the app's Production keys and an HTTPS callback (a go-live step).
      </div>

      <Drawer open={!!linkFor} onClose={() => setLinkFor(null)} wide>
        {linkFor && <LinkReviewPanel realmId={linkFor.realmId} companyName={linkFor.companyName}
          onClose={() => setLinkFor(null)} onApplied={load} />}
      </Drawer>
    </div>
  );
}

export { Sensors, Connections };
